// ClipRadar website — Ultra Search: a local LLM in front of the index.
// The demo is APP-TRUE and ALIVE: the same AppFrame chrome + search-box anatomy
// as the desktop-app mock, run as a looping state machine — type -> submit ->
// parse -> chips -> fused results -> hold -> next query. Reduced motion gets
// the finished state of query one, no loop.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { CRWIcon, type IconName } from '../components/icons';
import { AppFrame, ResultCard, SectionHead } from '../components/primitives';
import { useTyping } from '../components/hooks';
import { STILLS } from '../components/stills';

type UltraQuery = {
  q: string;
  files: number;
  parse: { icon: IconName; k: string; v: string }[];
  results: { img: string; pos: string; f: string; tc: string; dur: string; hot?: boolean }[];
};

// Both demos run on the real archival demo library; quoted speech is verbatim
// from the clips' own transcripts (speech.sqlite).
const QUERIES: UltraQuery[] = [
  {
    q: 'kennedy saying we choose to go to the moon at rice',
    files: 3,
    parse: [
      { icon: 'user', k: 'Person', v: 'John F. Kennedy' },
      { icon: 'mic', k: 'Said', v: '“we choose to go to the moon”' },
      { icon: 'image', k: 'Looks like', v: 'podium, stadium crowd' },
    ],
    results: [
      { img: STILLS.ricespeech, pos: '50% 30%', f: 'jfk-moon-rice-1962.mp4', tc: '00:00:18 - 00:00:32', dur: '14s', hot: true },
      { img: STILLS.ricehands, pos: '52% 28%', f: 'jfk-moon-rice-1962.mp4', tc: '00:00:47 - 00:00:54', dur: '7s' },
      { img: STILLS.inauguration, pos: '50% 30%', f: 'jfk-inauguration-1961.mp4', tc: '00:00:41 - 00:00:48', dur: '7s' },
      { img: STILLS.cubaaddress, pos: '50% 35%', f: 'jfk-cuba-address-1962.mp4', tc: '00:00:07 - 00:00:19', dur: '12s' },
    ],
  },
  {
    q: 'the astronauts on the meaning of the landing',
    files: 2,
    parse: [
      { icon: 'user', k: 'People', v: 'the Apollo 11 crew' },
      { icon: 'mic', k: 'Said', v: '“the meaning of this”' },
      { icon: 'image', k: 'Looks like', v: 'press conference desk' },
    ],
    results: [
      { img: STILLS.astronautsdesk, pos: '50% 40%', f: 'apollo11-press-conference-1969.mp4', tc: '00:00:00 - 00:00:10', dur: '10s', hot: true },
      { img: STILLS.pressdesk2, pos: '50% 40%', f: 'apollo11-press-conference-1969.mp4', tc: '00:00:48 - 00:00:57', dur: '9s' },
      { img: STILLS.pressdesk3, pos: '50% 40%', f: 'apollo11-press-conference-1969.mp4', tc: '00:00:20 - 00:00:29', dur: '9s' },
      { img: STILLS.moonwalkers, pos: '50% 40%', f: 'apollo11-moonwalk-1969.mp4', tc: '00:00:52 - 00:01:06', dur: '14s' },
    ],
  },
];

// Demo phases, in order. 'typing' length is governed by useTyping's done flag;
// every other phase advances on a fixed timer.
type Phase = 'typing' | 'submitting' | 'parsing' | 'chips' | 'results' | 'hold';
const PHASE_ORDER: Phase[] = ['typing', 'submitting', 'parsing', 'chips', 'results', 'hold'];
const PHASE_MS: Record<Exclude<Phase, 'typing'>, number> = {
  submitting: 350, parsing: 750, chips: 950, results: 1100, hold: 3200,
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

// Staged reveal — inline transition (NOT .crw-pop/.crw-fade: AppFrame's
// data-settled kills those after 3.6s, and this demo animates forever).
function Stage({ show, delay = 0, children, style }: { show: boolean; delay?: number; children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ opacity: show ? 1 : 0, transform: show ? 'none' : 'translateY(7px)', transition: `opacity 380ms var(--ease-out) ${delay}ms, transform 380ms var(--ease-out) ${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

// The real app's search box, as a living loop: type -> submit -> parse -> results.
function UltraDemo({ active, reduced }: { active: boolean; reduced: boolean }) {
  const [qi, setQi] = useState(0);
  const [phase, setPhase] = useState<Phase>(reduced ? 'hold' : 'typing');
  const query = QUERIES[qi];
  const { typed, done } = useTyping(reduced ? '' : query.q, { delay: 500, speed: 26 });

  // typing -> submitting once the sentence is fully typed
  useEffect(() => {
    if (reduced || !active || phase !== 'typing' || !done) return;
    const t = setTimeout(() => setPhase('submitting'), 180);
    return () => clearTimeout(t);
  }, [reduced, active, phase, done]);

  // every later phase advances on its timer; 'hold' wraps to the next query
  useEffect(() => {
    if (reduced || !active || phase === 'typing') return;
    const t = setTimeout(() => {
      if (phase === 'hold') { setQi((q) => (q + 1) % QUERIES.length); setPhase('typing'); }
      else setPhase(PHASE_ORDER[PHASE_ORDER.indexOf(phase) + 1]);
    }, PHASE_MS[phase]);
    return () => clearTimeout(t);
  }, [reduced, active, phase]);

  // has the loop reached phase p yet (within this cycle)?
  const at = (p: Phase) => reduced || PHASE_ORDER.indexOf(phase) >= PHASE_ORDER.indexOf(p);
  const shownQ = reduced ? query.q : typed;
  const boxLit = reduced || done;
  const modes: [string, IconName][] = [['Visuals', 'image'], ['Speech', 'mic'], ['Ultra', 'sparkle']];

  return (
    <AppFrame tab="Search" badge="ULTRA · LOCAL LLM">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* mode pills — Ultra active */}
        <div style={{ display: 'flex', gap: 2, padding: 2, borderRadius: 9, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', alignSelf: 'flex-start' }}>
          {modes.map(([m, ic]) => {
            const on = m === 'Ultra';
            return (
              <span key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 7, fontSize: 11.5, fontWeight: on ? 600 : 500, color: on ? 'var(--fg-1)' : 'var(--fg-3)', background: on ? '#27272A' : 'transparent', border: `1px solid ${on ? 'rgba(255,255,255,0.12)' : 'transparent'}` }}>
                <CRWIcon d={ic} size={11} />{m}
              </span>
            );
          })}
        </div>

        {/* search box — scope chips + typed sentence + camera + blue submit (presses at 'submitting') */}
        <div style={{ borderRadius: 12, background: '#0F0F10', border: '1px solid', borderColor: boxLit ? 'rgba(96,158,250,0.55)' : 'rgba(255,255,255,0.1)', boxShadow: boxLit ? '0 0 0 3px rgba(96,158,250,0.12), 0 0 22px rgba(96,158,250,0.12)' : 'none', transition: 'border-color 300ms var(--ease-out), box-shadow 300ms var(--ease-out)', padding: '9px 10px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--fg-1)', background: 'rgba(39,39,42,0.6)', border: '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="at" size={11} /> All analyzed media</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--fg-2)', background: 'rgba(39,39,42,0.6)', border: '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="user" size={11} /> People</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 6, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: 'var(--fg-4)', background: 'rgba(39,39,42,0.6)', border: '1px dashed rgba(255,255,255,0.12)' }}><CRWIcon d="ban" size={11} /> Exclude</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="crw-ultra-q" style={{ flex: 1, minWidth: 0, fontSize: 13, color: shownQ ? 'var(--fg-1)' : 'var(--fg-4)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {shownQ || 'Search your footage…'}{shownQ && !boxLit ? <span className="crw-caret"></span> : null}
            </span>
            {boxLit ? <span style={{ color: 'var(--fg-4)', display: 'inline-flex' }}><CRWIcon d="x" size={13} /></span> : null}
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 26, borderRadius: 7, border: '1px solid rgba(255,255,255,0.12)', color: 'var(--fg-3)', flex: 'none' }}><CRWIcon d="camera" size={13} /></span>
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 28, borderRadius: 8, background: 'var(--app-blue)', color: '#F2F9FF', flex: 'none', transform: phase === 'submitting' ? 'scale(0.88)' : 'none', boxShadow: phase === 'submitting' ? '0 0 0 4px rgba(51,104,208,0.35)' : '0 2px 8px rgba(51,104,208,0.45)', transition: 'transform 140ms var(--ease-out), box-shadow 140ms var(--ease-out)' }}><CRWIcon d="arrowRight" size={13} sw={2.2} /></span>
          </div>
        </div>

        {/* parse row — shimmer while the LLM 'reads', then app-styled chips pop in */}
        <div style={{ minHeight: 26, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {phase === 'parsing' && !reduced ? (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-2)' }}>reading the sentence…</span>
          ) : (
            <>
              <Stage show={at('chips')} style={{ display: 'inline-flex' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--fg-4)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Understood as</span>
              </Stage>
              {query.parse.map((p, i) => (
                <Stage key={`${qi}-${p.k}`} show={at('chips')} delay={i * 220} style={{ display: 'inline-flex' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, fontSize: 10.5, fontWeight: 500, whiteSpace: 'nowrap', color: '#9EC2FF', background: 'rgba(51,104,208,0.18)', border: '1px solid rgba(96,158,250,0.45)' }}>
                    <CRWIcon d={p.icon} size={11} />
                    <i style={{ fontStyle: 'normal', fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.7 }}>{p.k}</i>
                    {p.v}
                  </span>
                </Stage>
              ))}
              <Stage show={at('results')} style={{ display: 'inline-flex' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.08em', color: 'var(--fg-3)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>→ faces + speech + visuals, fused</span>
              </Stage>
            </>
          )}
        </div>

        {/* fused results — the app's result group + cards, streaming in */}
        <Stage show={at('results')} style={{ marginTop: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-2)', flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>“{query.q}” <i style={{ fontStyle: 'normal', color: 'var(--fg-4)' }}>in {query.files} files</i></span>
            <span style={{ color: 'var(--fg-4)', display: 'inline-flex', flex: 'none' }}><CRWIcon d="chevdown" size={12} /></span>
            <span className="crw-clearall" style={{ flex: 'none', fontSize: 10, color: 'var(--fg-3)', padding: '3px 9px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', whiteSpace: 'nowrap' }}>Copy {query.results.length} to clipboard</span>
          </div>
          <div className="crw-grid4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {query.results.map((r, i) => (
              <Stage key={`${qi}-${r.f}-${i}`} show={at('results')} delay={i * 120}>
                <ResultCard img={r.img} pos={r.pos} file={r.f} tc={r.tc} dur={r.dur} selected={r.hot} delay={0} />
              </Stage>
            ))}
          </div>
        </Stage>
      </div>
    </AppFrame>
  );
}

export function UltraSearch() {
  // run the loop only while the section is on screen
  const ref = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(false);
  const reduced = usePrefersReducedMotion();
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting), { threshold: 0.25 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const glow: CSSProperties = { position: 'absolute', left: '50%', top: '10%', transform: 'translateX(-50%)', width: 1000, height: 640, background: 'radial-gradient(ellipse, rgba(var(--light-rgb),0.08), rgba(var(--light-rgb),0.03) 55%, transparent 70%)', pointerEvents: 'none' };

  return (
    <section id="ultra" ref={ref} style={{ padding: '90px 24px', position: 'relative' }}>
      {/* one warm bloom — the ambient first-light for the LLM layer (one glow per viewport) */}
      <div aria-hidden="true" style={glow}></div>
      <div style={{ maxWidth: 1120, margin: '0 auto', position: 'relative' }}>
        {/* A1: no accent-colored headline words (matches the hero ruling) */}
        <SectionHead center label="Ultra Search" title={<>Type it like you’d say it.</>}
          sub="A local LLM reads the whole sentence — the person, the quote, the shot — and runs every search mode at once." />
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <UltraDemo active={active} reduced={reduced} />
        </div>
        {/* three quiet claims in the app's mono voice — the demo above is the show */}
        <div className="crw-ultra-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28, marginTop: 40, maxWidth: 860, marginLeft: 'auto', marginRight: 'auto' }}>
          {([
            ['Reads, not matches', 'A person, a quote and a place — each searched the right way.'],
            ['Every mode at once', 'Faces, speech and visuals, fused into one best-first list.'],
            ['Local, like everything', 'The LLM runs on your GPU. Nothing leaves the machine.'],
          ] as [string, string][]).map(([t, s]) => (
            <div key={t} style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 500, letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase', color: 'var(--fg-2)' }}>{t}</span>
              <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg-3)', textWrap: 'pretty' }}>{s}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
